import { z } from 'zod';
import { IdSchema, InstantSchema } from '../common/index.js';

/**
 * Vidéos du dossier (PARITE-5, démo facturation-ged/VideosDossier, réunion du 16/10 #54), ajout au contrat 1.0.0. Une vidéo est
 * une ADRESSE YouTube (aucun fichier : rien n'entre dans la GED), lue par le lecteur sans cookie de suivi (youtube-nocookie.com).
 * Réservée à l'équipe ; le partage hors équipe (portail de la famille) passe par un bouton explicite et une alerte qui liste les
 * destinataires : la liste affichée est envoyée et enregistrée. Le portail ne reçoit que le titre et le lecteur.
 */
const Identifiant = z.string().regex(/^[A-Za-z0-9_-]{11}$/);
const Lecteur = z.string().regex(/^https:\/\/www\.youtube-nocookie\.com\/embed\/[A-Za-z0-9_-]{11}$/);

export const VideoDossierSchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    title: z.string().min(1),
    url: z.string().min(1),
    youtubeId: Identifiant,
    embedUrl: Lecteur,
    createdAt: InstantSchema,
    createdByDisplayName: z.string(),
    sharedWithFamilyAt: InstantSchema.nullable(),
    sharedByDisplayName: z.string().nullable(),
    /** Destinataires affichés dans l'alerte au moment du partage (noms), vide tant que la vidéo n'est pas partagée. */
    sharedWith: z.array(z.string()),
    rowVersion: z.number().int().min(1),
  })
  .meta({ id: 'VideoDossier', description: 'Vidéo YouTube rattachée au dossier (équipe MAAT).' });

export const VideoCreateSchema = z
  .object({ title: z.string().trim().min(1).max(120), url: z.string().trim().min(1).max(500) })
  .meta({ id: 'VideoCreate', description: 'Titre et adresse YouTube (https).' });

export const VideoRecipientSchema = z
  .object({ userId: IdSchema, displayName: z.string().min(1), role: z.enum(['heritier', 'mandant']) })
  .meta({ id: 'VideoRecipient', description: 'Compte du portail qui verra une vidéo partagée.' });

export const VideosDossierSchema = z
  .object({ videos: z.array(VideoDossierSchema), recipients: z.array(VideoRecipientSchema) })
  .meta({ id: 'VideosDossier', description: 'Vidéos du dossier et destinataires possibles d’un partage au portail.' });

export const VideoShareSchema = z
  .object({
    recipients: z.array(z.object({ userId: IdSchema, displayName: z.string().min(1) })).min(1),
    acknowledgedRecipients: z.array(z.string().min(1)).min(1),
  })
  .refine((s) => s.recipients.every((r) => s.acknowledgedRecipients.includes(r.displayName)), {
    message: 'chaque destinataire doit avoir été affiché dans l’alerte',
    path: ['acknowledgedRecipients'],
  })
  .meta({ id: 'VideoShare', description: 'Partage au portail de la famille : la liste affichée dans l’alerte.' });

export const PortalVideoSchema = z
  .strictObject({ id: IdSchema, title: z.string().min(1), embedUrl: Lecteur })
  .meta({ id: 'PortalVideo', description: 'Vidéo partagée par le cabinet : titre et lecteur seulement.' });
